// Reveal beat (Gate 1 items C and H): ALL its timing and every effect
// parameter, per preset, so the motion and the look can be tuned to the
// reference clips (criterion #11) by editing only this file (and beats/).
// Times are in ms; distances in hex radii (R); speeds in R per second.
// Easing names: see beats/easing.js.
//
// Particle COUNTS are graded load and live in load.config.js: 400 impact
// sprites alive at the peak, a 60-particle missile trail. The explosion
// flipbook, smoke and debris sprites come OUT of the 400, not on top: the
// sparks get whatever the other three leave (beats/effectBudget.js), so every
// preset peaks at exactly 400. Bloom and heat distortion are post effects
// (added load, allowed). Each effect has `enabled`; turning one off gives its
// share of the 400 back to the sparks.

// Flipbook sheets in src/gate1/sprites/. A sheet is a grid of `cols` columns
// of frameW x frameH frames, read left to right, top to bottom, starting at
// cell `first`. Credits: src/gate1/sprites/CREDITS.md.
export const SHEETS = {
    // StumpyStrust "More Explosions" (CC0): radial fireball, then ~29 frames
    // of trailing grey smoke (frames 30-58).
    explosion1: { file: 'Impact_explosion1.png', frameW: 100, frameH: 100, cols: 10, first: 0, frames: 59 },
    // Sinestesia "2D Explosion Animations" (CC0): cartoon blast with a baked
    // red glow, blowing sideways (to the right); no smoke.
    explosion2: { file: 'Impact_explosion2.png', frameW: 512, frameH: 512, cols: 8, first: 2, frames: 62 },
}

// ── hog (the default; the graded build) ─────────────────────────────────────
// Pre-impact from Tank Stars, post-impact from Boom Beach.
const hog = {
    leadMs: 89,                  // tap -> launch (the attacker's shot leaves)

    clash: {                     // one exchange: attacker and defender shots meet past the band
        enabled: true,
        riseMs: 880,             // attacker shot from its corridor to the meeting point (lab MISSILE_RISE_MS)
        riseEase: 'cubicOut',
        responseDelayMs: 620,    // defender fires this long after the attacker (lab MISSILE_RESPONSE_MS)
        meetOffsetR: 1.2,        // meeting point: this far past the band into the defender's Hive
        burstParticles: 80,      // collision burst (spark sprites, dead long before the impact)
        burstLifeMs: 420,
        burstSpeedR: 9,
        gapMs: 260,              // pause after the collision (lab MISSILE_EXCHANGE_GAP_MS)
    },

    missile: {                   // the surplus shot: a lob, launch -> crest -> impact
        riseMs: 1380,            // launch -> crest (slow climb)
        riseEase: 'quadOut',
        fallMs: 440,             // crest -> impact (fast drop); crest at 76% of the flight
        fallEase: 'quadIn',
        bowR: 2.5,               // sideways bow of the ground path
        liftR: 1.2,              // how far up the screen the crest lifts the missile
        crestScale: 1.6,         // missile drawn this much larger at the crest (nearer the camera)
        trailSpacingMs: 7,       // the trail particles sit this far apart in time along the path
    },

    anticipation: {              // the missile stretches lengthwise just before impact (held through the hit-stop)
        enabled: true,
        stretchMs: 118,          // over the last stretchMs of the fall
        ease: 'quadIn',
        length: 1.25,            // stretched length (x its size)
        width: 0.9,              // stretched width (thinner, so it reads as speed)
    },

    hitStopMs: 59,               // freeze the scene at impact, then resume (0 = none)

    flash: { enabled: true, ms: 30, sizeR: 4 },    // white flash at the aim cell, from impact

    blast: {
        ms: 207,
        after: 'impact',         // 'impact' or 'flash' (when the flash ends); never before the hit-stop ends
    },

    explosion: {                 // flipbook at the aim cell, through the blast
        enabled: true,
        sheet: 'explosion1',
        frames: [0, 26],         // first and last sheet frame played (fireball only; the smoke tail is the smoke layer)
        step: 2,                 // use every 2nd frame (14 frames: half the texture memory)
        fps: 68,                 // 14 frames in ~207 ms = the blast
        sizeR: 9,                // on-screen size; the atlas frame is 2x this in device pixels (item F)
        blend: 'normal',
    },

    sparks: {                    // the additive particle burst (all the impact sprites the others leave)
        emitMs: 120,             // all emitted within this window from the blast's start
        lifeMs: [520, 900],
        speedR: [4, 14],
        drag: 2.4,               // per second
        sizeR: [0.27, 0.63],     // particle size range
    },

    settleMs: 1790,              // from the end of the blast

    smoke: {                     // puffs through the settle, from the sheet's smoke frames
        enabled: true,
        sheet: 'explosion1',     // null: a generated placeholder puff
        frames: [30, 58],
        step: 4,                 // 8 frames; each puff plays them once over its life
        count: 14,
        sizeR: 4.5,
        leadMs: 80,              // the puffs start this long before the blast ends (crossfade)
        spawnMs: 40,             // all puffs born within this window
        spreadR: 1.6,            // born within this radius of the aim cell
        driftR: [0.3, 1.0],      // outward drift speed
        riseR: 0.5,              // upward drift speed (screen)
        growTo: 1.8,             // scale at the end of the settle
        alpha: 0.85,
        fadeInMs: 120,
        fadeOutMs: 700,          // fades out over the last part of the settle
    },

    debris: {                    // chunks thrown out at the blast, landing through the settle
        enabled: true,
        sprite: 'debris.png',    // a horizontal strip of `variants` square frames in sprites/; missing = placeholder
        variants: 4,
        count: 40,
        sizeR: [0.35, 0.8],
        speedR: [3, 9],          // outward ground speed
        upR: [5, 12],            // initial upward speed (fake height: drawn as lift + scale)
        gravityR: 40,
        friction: 6,             // per second, once landed
        spinPerS: 14,            // radians per second, max either way, while airborne
        fadeOutMs: 500,          // over the last part of the settle
    },

    bloom: {                     // glow on the explosion and flash (post effect)
        enabled: true,
        threshold: 0.5,          // brightness above which pixels glow
        strength: 1.6,           // glow added on top
        blurR: 0.7,              // glow spread
        quality: 3,              // blur passes
        decayMs: 500,            // after the blast, the glow fades over this
    },

    distortion: {                // heat haze around the impact (post effect, whole scene)
        enabled: true,
        strengthR: 0.6,          // peak pixel offset (was 0.22: hard to see on a phone)
        radiusR: 6,
        durationMs: 1500,        // from the blast's start
        ease: 'sineIn',          // strength falls off with this (holds, then fades)
        spinPerS: 4,             // the haze pattern turns this fast (motion is what reads as shimmer)
        mapPx: 128,              // displacement map texture size (its own texture)
    },

    shake: {                     // camera shake (the whole scene), from the end of the hit-stop
        enabled: true,
        durationMs: 2000,
        amplitudeR: 0.45,
        ease: 'cubicOut',        // amplitude = amplitudeR * (1 - ease(t))
        frequencyHz: 17,
    },

    crater: {                    // rings from the blast's start
        ringDelayMs: 90,         // each ring starts this long after the one inside it (lab FIRE_SWEEP_MS_PER_CELL)
        cellGrowMs: 260,
        cellEase: 'backOut',
        cellStartScale: 0.35,
    },

    holdMs: 500,                 // after everything settles, before the beat ends
}

// Deep merge for presets that start from hog.
const isObj = v => v && typeof v === 'object' && !Array.isArray(v)
function from(base, over) {
    const out = { ...base }
    for (const [k, v] of Object.entries(over)) out[k] = isObj(v) && isObj(base[k]) ? from(base[k], v) : v
    return out
}

// ── clashRoyale (to be tuned to its clip) ───────────────────────────────────
const clashRoyale = from(hog, {
    leadMs: 1060,                // tap -> missile visible
    missile: { riseMs: 1530, riseEase: 'sineOut', fallMs: 1470, fallEase: 'sineIn' },
    anticipation: { enabled: false },
    hitStopMs: 0,
    flash: { ms: 59 },
    blast: { ms: 266, after: 'impact' },
    explosion: { fps: 53 },      // 14 frames in ~266 ms
    shake: { durationMs: 1530, ease: 'expoOut' },  // quick ease-out
    settleMs: 2030,
})

// ── tankStars (to be tuned to its clip) ─────────────────────────────────────
const tankStars = from(hog, {
    leadMs: 89,
    missile: { riseMs: 1380, fallMs: 440 },
    anticipation: { enabled: false },
    hitStopMs: 0,
    flash: { ms: 89 },
    blast: { ms: 531, after: 'flash' },
    explosion: { fps: 26 },      // 14 frames in ~531 ms
    shake: { durationMs: 266, ease: 'quadOut' },
    settleMs: 1650,
})

export const PRESETS = { hog, clashRoyale, tankStars }

// The preset the app plays (and the graded build uses).
export const DEFAULT_PRESET = 'hog'
export const REVEAL = PRESETS[DEFAULT_PRESET]
